import { ReactElement, useState } from "react";
import {
  Button,
  H5,
  RadioSelector,
  RadioSelectorOption,
} from "@sellar-io/components";
import { SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrderCreateBuyerInvoice } from "../../../hooks/api/v2/order/useOrderCreateBuyerInvoice";
import { useToast } from "../../../components/toast/useToast.shared";
import { Toast } from "../../../components/toast/Toast";
import { useSellarUser } from "../../../hooks";

import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { downloadPDF } from "../../../utils/downloadHelpers";
import { useOrder } from "../../../hooks/api/v2/order/useOrder";
import { createInvoiceFileName } from "./OrderCreateInvoiceDialog.shared";

type GeneratePdfOptionsType = "sendToUser" | "sendToBusiness" | "download";

export function OrderCreateInvoiceDialog({
  active,
  orderId,
  onDismiss,
  retailerInvoiceEmails,
}: {
  active: boolean;
  orderId: number;
  onDismiss: () => void;
  retailerInvoiceEmails?: string[];
}): ReactElement {
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const { toast } = useToast();
  const buyerInvoiceMutation = useOrderCreateBuyerInvoice(orderId);
  const isBuyer = business?.type === "retailer";
  const [generateOption, setGenerateOption] = useState<GeneratePdfOptionsType>(
    isBuyer ? "sendToUser" : "sendToBusiness",
  );
  const getEmails = () => {
    if (generateOption === "sendToUser") return user?.email;
    return retailerInvoiceEmails?.join(", ");
  };

  const orderQuery = useOrder(orderId);
  const order = orderQuery.data;

  const download = () => {
    buyerInvoiceMutation.mutate(
      {
        sendToUser: generateOption === "sendToUser",
        directDownload: generateOption === "download",
      },
      {
        onSuccess: (pdf) => {
          if (generateOption === "download") {
            const fileName = order
              ? createInvoiceFileName({
                  orderId,
                  supplierName: order.Supplier.Business.name,
                  buyerName: order.Retailer.Business.name,
                  confirmedAt: order.confirmedAt,
                  invoiceIssuedAt: order.invoiceIssuedAt,
                  customInvoiceNumber: order.customInvoiceNumber,
                  paymentDue: order.paymentDue,
                })
              : `${orderId}-${business?.name}-VAT-Invoice`;

            downloadPDF(fileName, pdf);
          } else {
            toast({
              placement: "bottom",
              render: () => (
                <Toast icon={"CHECK"}>
                  {`An invoice will be sent to ${getEmails()} shortly.`}
                </Toast>
              ),
            });
          }

          onDismiss();
        },
      },
    );
  };
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title="VAT invoice"
      />
      <DialogContent>
        {buyerInvoiceMutation.isError ? (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(buyerInvoiceMutation.error)?.response?.data
                .message ?? "An unknown error occurred"
            }
          />
        ) : (
          <>{buyerInvoiceMutation.isPending && <SpinnerLegacy />}</>
        )}
        {!buyerInvoiceMutation.isPending && (
          <RadioSelector
            onChange={(e) => {
              setGenerateOption(
                e.currentTarget.value as GeneratePdfOptionsType,
              );
            }}
            value={generateOption.toString()}
            required={true}
          >
            <RadioSelectorOption value={"sendToBusiness"}>
              <H5>{isBuyer ? "Send to accounts" : "Send reminder to them"}</H5>
              {`A PDF will be sent to: ${retailerInvoiceEmails?.join(", ")}`}
            </RadioSelectorOption>
            <RadioSelectorOption value={"sendToUser"}>
              <H5>Send to me</H5>
              {`A PDF will be sent to: ${user?.email}`}
            </RadioSelectorOption>
            <RadioSelectorOption value={"download"}>
              <H5>Download</H5>A PDF will be downloaded directly to your device.
            </RadioSelectorOption>
          </RadioSelector>
        )}
        {buyerInvoiceMutation.isPending && generateOption === "download" && (
          <>
            Download can take up to 10 seconds. Please do not navigate away
            until your download is complete.
          </>
        )}
      </DialogContent>
      <DialogCTA>
        <Button onClick={download}>Confirm</Button>
      </DialogCTA>
    </Dialog>
  );
}
